import { useEffect, useRef } from "react";

import { useSetPreferredCurrency } from "@/Apollo/hooks/useSetPreferredCurrency";
import { Currency } from "@/Apollo/schema";
import { useAuth } from "@/auth";
import { DISPLAY_CURRENCY_EVENT } from "@/features/display-currency/display-currency.static";
import { useDisplayCurrencyStore } from "@/features/display-currency/display-currency.store";
import {
  loginSyncAction,
  toKnownCurrency,
} from "@/features/display-currency/display-currency.sync";
import { useDisplayCurrencyEnabled } from "@/features/display-currency/useDisplayCurrency";
import { logStatsigEvent } from "@/features/statsig/statsig-analytics-client";
import useMe from "@/scenes/auth/hooks/useMe";

/** The session's last `DISPLAY_CURRENCY_EVENT`, as `source:currency`, so it is logged once. */
const LOGGED_KEY = "display_currency_logged";

/** Whether this source and currency still need logging this session; marks them logged. */
const firstThisSession = (entry: string) => {
  try {
    if (window.sessionStorage.getItem(LOGGED_KEY) === entry) {
      return false;
    }

    window.sessionStorage.setItem(LOGGED_KEY, entry);
  } catch {
    /* Storage blocked: log once per page load instead. */
  }

  return true;
};

/** Answers with the first guess from the visitor's country (`pages/api/display-currency-guess`). */
export const DISPLAY_CURRENCY_GUESS_PATH = "/api/display-currency-guess";

const fetchGuess = async (): Promise<Currency> => {
  try {
    const response = await fetch(DISPLAY_CURRENCY_GUESS_PATH);
    const body = (await response.json()) as { currency?: unknown };

    return toKnownCurrency(body.currency) ?? Currency.Eur;
  } catch {
    return Currency.Eur;
  }
};

/**
 * Keeps the display currency between visits and in step with the account (WHO-5143). Mounted
 * once, in `_app`:
 *
 * - reads the buyer's pick from the cookie, and asks for a first guess from their country when
 *   there is none (never saved on the account: only a pick is; a non-euro guess is kept in its own
 *   day-long cookie, so the edge can serve the right variant from the next page view, WHO-6199);
 * - once per signed-in member, the account's currency wins, and a guest's pick is saved on an
 *   account that has none (`loginSyncAction`). A pick made while signed in is saved when it is
 *   made (`useSetDisplayCurrency`);
 * - logs which currency the visitor sees and whether it was picked or guessed
 *   (`DISPLAY_CURRENCY_EVENT`, WHO-6199).
 *
 * Nothing happens while the gate is off: no guess, no cookie, no sync, as the apps' production
 * builds (an account preference set elsewhere must not switch conversion on here).
 */
export const DisplayCurrencySync = () => {
  const enabled = useDisplayCurrencyEnabled();
  const { isLoggedIn } = useAuth();
  const { data } = useMe();
  const { picked, guess, hydrated, pickedSignedIn } = useDisplayCurrencyStore();
  const { hydrate, setGuess, pick } = useDisplayCurrencyStore.getState();
  const [setPreferredCurrency] = useSetPreferredCurrency();
  /** Asked once per page load. */
  const guessRequested = useRef(false);
  /** The member whose sign-in has been reconciled this page load. */
  const syncedMember = useRef<string | null>(null);

  useEffect(() => {
    if (enabled) {
      hydrate();
    }
  }, [enabled, hydrate]);

  useEffect(() => {
    if (!enabled || !hydrated || picked || guess || guessRequested.current) {
      return;
    }

    guessRequested.current = true;
    void fetchGuess().then(setGuess);
  }, [enabled, hydrated, picked, guess, setGuess]);

  useEffect(() => {
    const currency = picked ?? guess;

    if (!enabled || !hydrated || !currency) {
      return;
    }

    const source = picked ? "picked" : "guessed";

    if (firstThisSession(`${source}:${currency}`)) {
      logStatsigEvent(DISPLAY_CURRENCY_EVENT, currency, { source });
    }
  }, [enabled, hydrated, picked, guess]);

  const me = data?.me;

  useEffect(() => {
    if (!enabled || !hydrated || !isLoggedIn || !me) {
      return;
    }

    if (syncedMember.current === me.id) {
      return;
    }

    syncedMember.current = me.id;
    const action = loginSyncAction({
      account: me.preferred_currency,
      picked,
    });

    if (action.type === "adopt") {
      // A pick made while signed in, before `me` loaded, was already sent to the account; the
      // account's value here predates it (WHO-5143 review).
      if (!pickedSignedIn) {
        pick(action.currency);
      }
    } else if (action.type === "push") {
      setPreferredCurrency({ variables: { currency: action.currency } }).catch(
        error =>
          console.warn("display currency: saving on the account failed", error)
      );
    }
  }, [
    enabled,
    hydrated,
    isLoggedIn,
    me,
    picked,
    pickedSignedIn,
    pick,
    setPreferredCurrency,
  ]);

  return null;
};
